import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Flag, Lock, Unlock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Label } from '@/components/ui/Label'
import { LiftDialog } from '@/features/blocks/components/LiftDialog'
import { useCanAny } from '@/lib/permissions'
import { useFlagAccountSuspicious } from '../hooks/useAccountDetail'
import type { AccountDetail } from '../types'

interface Props {
  account: AccountDetail
}

const FLAG_REASON_MIN = 10

/**
 * AF-132 — action buttons for the account detail.
 *
 * Apply PND routes to the AF-128 BlockRequest with type=account, the
 * NUBAN, bank code, and a reason prefixed `PND:` so the maker-checker
 * flow takes over (per technical notes — no parallel PND workflow).
 *
 * Lift PND opens AF-128's LiftDialog against the account's active PND
 * block. Flag Suspicious creates a manual alert and navigates to it.
 *
 * Permission gates:
 *   - Apply PND  → blocks:request   (a.k.a. pnd_request)
 *   - Lift PND   → blocks:lift      (a.k.a. pnd_approve)
 *   - Flag      → blocks:request    (any analyst with case-touch privileges)
 */
export function AccountActions({ account }: Props) {
  const navigate = useNavigate()
  const canRequest = useCanAny('blocks:request')
  const canLift = useCanAny('blocks:lift')

  const [liftOpen, setLiftOpen] = useState(false)
  const [flagOpen, setFlagOpen] = useState(false)

  const onApplyPnd = () => {
    const params = new URLSearchParams({
      type: 'account',
      bank_code: account.bank.cbn_code,
      nuban: account.nuban,
      reason: `PND: customer-requested hold on ${account.bank.short_name} · ${account.nuban}.`,
      source: 'manual',
      source_reference: account.customer.customer_number,
    })
    void navigate(`/blocks/new?${params.toString()}`)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Actions</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {!account.pnd ? (
          <ActionButton
            icon={<Lock size={14} aria-hidden />}
            label="Apply PND"
            onClick={onApplyPnd}
            disabled={!canRequest}
            disabledReason="You don't have the blocks:request permission."
          />
        ) : (
          <ActionButton
            icon={<Unlock size={14} aria-hidden />}
            label="Lift PND"
            onClick={() => setLiftOpen(true)}
            disabled={!canLift || account.pnd_block_id === null}
            disabledReason={
              !canLift
                ? "You don't have the blocks:lift permission."
                : 'No active PND block linked to this account.'
            }
          />
        )}
        <ActionButton
          icon={<Flag size={14} aria-hidden />}
          variant="outline"
          label="Flag as Suspicious"
          onClick={() => setFlagOpen(true)}
        />
      </CardContent>

      {liftOpen && account.pnd_block_id && account.pnd_block_number && (
        <LiftDialog
          open={liftOpen}
          onOpenChange={setLiftOpen}
          blockId={account.pnd_block_id}
          blockNumber={account.pnd_block_number}
          isRegulatory={account.pnd_is_regulatory}
          onLifted={() => setLiftOpen(false)}
        />
      )}

      <FlagSuspiciousDialog
        open={flagOpen}
        onOpenChange={setFlagOpen}
        account={account}
      />
    </Card>
  )
}

function ActionButton({
  icon,
  label,
  onClick,
  disabled,
  disabledReason,
  variant = 'primary',
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
  disabledReason?: string
  variant?: React.ComponentProps<typeof Button>['variant']
}) {
  if (disabled && disabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button variant={variant} disabled>
              {icon}
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    )
  }
  return (
    <Button variant={variant} onClick={onClick} disabled={disabled}>
      {icon}
      {label}
    </Button>
  )
}

interface FlagDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  account: AccountDetail
}

function FlagSuspiciousDialog({ open, onOpenChange, account }: FlagDialogProps) {
  const navigate = useNavigate()
  const [reason, setReason] = useState('')
  const flag = useFlagAccountSuspicious()

  const submit = () => {
    if (reason.trim().length < FLAG_REASON_MIN) return
    flag.mutate(
      {
        customer_id: account.customer.id,
        nuban: account.nuban,
        reason: reason.trim(),
      },
      {
        onSuccess: ({ alert_id }) => {
          onOpenChange(false)
          setReason('')
          void navigate(`/alerts/${alert_id}`)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-status-pending" aria-hidden />
            Flag account as suspicious
          </DialogTitle>
          <DialogDescription>
            Creates a manual alert tied to{' '}
            <span className="font-mono text-xs">{account.bank.short_name} · {account.nuban}</span>.
            Recorded in the audit log; you can pick it up from the alerts queue.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          <Label htmlFor="flag-reason">Reason</Label>
          <textarea
            id="flag-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What did you observe? Sent to the assigned analyst."
            className="min-h-[100px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <p className="text-xs text-muted-fg">Min {FLAG_REASON_MIN} characters.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={reason.trim().length < FLAG_REASON_MIN || flag.isPending}
            onClick={submit}
          >
            {flag.isPending ? 'Flagging…' : 'Flag account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
